import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-slate-800 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none text-gray-700">
          <p className="text-lg mb-6">
            Last updated: November 1, 2025
          </p>

          <p className="mb-6">
            HealthCity is committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, and protect your information when you use our services.
          </p>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Information We Collect</h2>
          <h3 className="text-xl font-semibold text-slate-700 mb-3">Personal Information</h3>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>Name, email address, phone number</li>
            <li>Date of birth and demographic information</li>
            <li>Medical history and health information relevant to your treatment</li>
            <li>Payment information (processed securely through third-party providers)</li>
            <li>Communication preferences and appointment history</li>
          </ul>

          <h3 className="text-xl font-semibold text-slate-700 mb-3">Technical Information</h3>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>IP address and browser information</li>
            <li>Website usage data and analytics</li>
            <li>Session recordings for online consultations (with consent)</li>
            <li>Device information and access logs</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">How We Use Your Information</h2>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>To provide and improve our healthcare services</li>
            <li>To communicate with you about appointments and treatments</li>
            <li>To process payments and manage billing</li>
            <li>To comply with legal and regulatory requirements</li>
            <li>To maintain accurate medical records</li>
            <li>To send service updates and important notifications</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Information Sharing</h2>
          <p className="mb-4">
            We do not sell, trade, or share your personal information with third parties except:
          </p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>With your explicit consent</li>
            <li>For emergency medical situations</li>
            <li>To comply with legal obligations</li>
            <li>With healthcare professionals involved in your care</li>
            <li>With secure payment processors for transaction processing</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Data Security</h2>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>SSL encryption for all data transmission</li>
            <li>Secure servers with regular security updates</li>
            <li>Access controls and authentication measures</li>
            <li>Regular security audits and assessments</li>
            <li>Staff training on data protection protocols</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Your Rights</h2>
          <p className="mb-4">Under GDPR and UK data protection laws, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 mb-6">
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your data (subject to legal requirements)</li>
            <li>Restrict processing of your data</li>
            <li>Data portability</li>
            <li>Object to processing</li>
          </ul>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Cookies and Tracking</h2>
          <p className="mb-4">
            We use cookies and similar technologies to improve your experience on our website. 
            You can control cookie settings through your browser preferences.
          </p>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Data Retention</h2>
          <p className="mb-6">
            We retain your personal information for as long as necessary to provide our services 
            and comply with legal obligations. Medical records are retained according to professional 
            guidelines and regulatory requirements.
          </p>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Contact Us</h2>
          <p className="mb-4">
            If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p><strong>Email:</strong> privacy@healthcity.org.uk</p>
            <p><strong>Phone:</strong> 0333 800 3006</p>
            <p><strong>Address:</strong> Data Protection Officer, HealthCity, [Address]</p>
          </div>

          <h2 className="text-2xl font-semibold text-slate-800 mt-8 mb-4">Changes to This Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. We will notify you of any significant 
            changes via email or through our website. Your continued use of our services constitutes 
            acceptance of the updated policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;